/**
 * In-memory snapshot store for the overview screens (Nutzerübersicht, Kurse,
 * Kursbuchungen, Mitgliedschaften, Mitgliedschaftsbuchungen).
 *
 * The sub-screens inside the four shells are bare `<Route exact>` siblings, so
 * a list screen unmounts as soon as the user opens a detail page and its whole
 * local state (page, filters, search, accumulated rows) is destroyed. This
 * module keeps that state alive for the duration of the session so returning
 * from a detail page can render instantly at the same scroll position.
 *
 * Deliberately memory-only: `RowData.cells[].value` holds JSX and the raw
 * payloads carry Firebase `{_seconds}` timestamps — neither survives
 * serialisation, so sessionStorage is not an option.
 */

export type ListCacheEntry = {
  /** Whitelisted slice of the screen's state. */
  state: any;
  /** scrollTop of the shell's scroll container when the screen was left. */
  scrollTop: number;
  /** window.location.pathname at save time, i.e. where the user went. */
  exitTo: string;
  savedAt: number;
};

const TTL_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 20;
const RECENT_PATHS = 3;

const cache = new Map<string, ListCacheEntry>();

/** Most recently visited distinct pathnames, newest first. */
let recentPaths: string[] = [];
let historyAttached = false;

function normalizeKey(key: string) {
  return key.length > 1 && key.charAt(key.length - 1) === "/"
    ? key.slice(0, -1)
    : key;
}

/**
 * Registered once by MainProvider. Consecutive pushes to the same pathname are
 * collapsed so that the participants screens' own `?page=&monthNum=` pushes do
 * not flush the window.
 */
export function attachHistory(history: any) {
  if (historyAttached) return;
  historyAttached = true;
  recentPaths = [window.location.pathname];
  history.listen((location: any) => {
    if (recentPaths[0] === location.pathname) return;
    recentPaths = [location.pathname].concat(recentPaths).slice(0, RECENT_PATHS);
  });
}

/**
 * The shell's scrolling element. Walks up from the screen's own node because
 * `id="scroll-container"` only exists in the Branch and Club shells — the two
 * Nutzerübersicht screens live in BranchSettings/ClubSettings, which only carry
 * the class.
 */
export function getScrollContainer(
  node: HTMLElement | null,
  selector = ".br-inner-container"
): HTMLElement | null {
  if (node) {
    // The shells' scroller is an ancestor of the screen; a screen that brings
    // its own scrollport (the Bereichsfeed, via InfiniteScroll's `height`)
    // has it as a descendant instead.
    const up = node.closest ? node.closest(selector) : null;
    if (up) return up as HTMLElement;
    const down = node.querySelector(selector);
    if (down) return down as HTMLElement;
  }
  return document.querySelector(selector);
}

export function saveListState(
  key: string,
  state: any,
  node: HTMLElement | null,
  selector?: string
) {
  const container = getScrollContainer(node, selector);
  const normalized = normalizeKey(key);
  cache.delete(normalized);
  cache.set(normalized, {
    state,
    scrollTop: container ? container.scrollTop : 0,
    exitTo: window.location.pathname,
    savedAt: Date.now(),
  });
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/**
 * Side-effect free on purpose: React.StrictMode is on and React 17 invokes
 * constructors twice in development, so this must not consume the entry.
 *
 * Returns the entry only when the user actually came back from the page this
 * list was left for. `history.action` cannot be used — every in-app back link
 * is a `<Link>` (PUSH) and the create/edit screens return via `<Redirect>`
 * (REPLACE), so a POP never happens.
 */
export function restoreListState(key: string): ListCacheEntry | null {
  const entry = cache.get(normalizeKey(key));
  if (!entry) return null;
  if (Date.now() - entry.savedAt > TTL_MS) return null;
  // `history.push` updates window.location before notifying listeners, and the
  // Router (listener #0) re-renders synchronously inside its own listener when
  // the push is not batched — so our buffer may or may not have advanced by the
  // time this runs. Accepting either of the two newest paths covers both
  // orderings; a miss just means a normal fresh load.
  if (entry.exitTo !== recentPaths[0] && entry.exitTo !== recentPaths[1]) {
    return null;
  }
  return entry;
}

export function dropListState(key: string) {
  cache.delete(normalizeKey(key));
}

export function clearListStateCache() {
  cache.clear();
}

/**
 * Re-asserts the saved scrollTop across a few frames while the content settles
 * (card lists grow as their images decode), then stops. Aborts as soon as the
 * user scrolls themselves. Returns a cancel function for componentWillUnmount.
 */
export function restoreScrollPosition(
  entry: ListCacheEntry,
  node: HTMLElement | null,
  maxFrames = 5,
  onSettled?: () => void,
  selector?: string
): () => void {
  const container = getScrollContainer(node, selector);
  if (!container || entry.scrollTop <= 0) {
    if (onSettled) onSettled();
    return () => {};
  }

  // `.br-inner-container` has `scroll-behavior: smooth`, which would animate
  // every assignment. `scrollTo({behavior:"auto"})` does not help — "auto"
  // means "use the computed CSS value" — and `"instant"` is absent from the
  // ScrollBehavior type in TS 4.2. An inline style beats the stylesheet rule.
  const prevBehavior = container.style.scrollBehavior;
  container.style.scrollBehavior = "auto";

  let rafId = 0;
  let done = false;
  let frame = 0;
  let lastWritten = -1;
  let lastHeight = -1;
  let stableFrames = 0;

  function finish() {
    if (done) return;
    done = true;
    if (rafId) cancelAnimationFrame(rafId);
    (container as HTMLElement).style.scrollBehavior = prevBehavior;
    if (onSettled) onSettled();
  }

  function step() {
    if (done || !container) return;
    // Never fight a user who has started scrolling.
    if (lastWritten >= 0 && Math.abs(container.scrollTop - lastWritten) > 2) {
      finish();
      return;
    }

    const max = Math.max(0, container.scrollHeight - container.clientHeight);
    const target = Math.min(entry.scrollTop, max);
    container.scrollTop = target;
    lastWritten = container.scrollTop;

    stableFrames = container.scrollHeight === lastHeight ? stableFrames + 1 : 0;
    lastHeight = container.scrollHeight;
    frame += 1;

    const reached = lastWritten >= target - 2;
    if ((stableFrames >= 2 && reached) || frame >= maxFrames) {
      finish();
      return;
    }
    rafId = requestAnimationFrame(step);
  }

  // Run once synchronously: componentDidMount is after the DOM mutation but
  // before paint, so this avoids a visible flash at the top of the list.
  step();
  if (!done) rafId = requestAnimationFrame(step);

  return finish;
}
